import axios, { AxiosError } from 'axios';
import { errorMessage } from 'store/api/portalAPI';
import { errorAlert } from './alert';

/**
 *
 * @param url
 * @param fileName must contain file extension
 * @param openInNewTab
 * @returns {void}
 */
const downloadFileRaw = async (
    url: string,
    fileName: string | undefined = undefined,
    openInNewTab = false,
    rethrowErrors = false
): Promise<void> => {
    return axios
        .get(url, {
            responseType: 'blob',
            withCredentials: true,
        })
        .then(async (response) => {
            const mimeType = response.headers['content-type'] || response.headers['Content-Type'];
            const contentDispositionFilename = extractFilename(
                response.headers['content-disposition'] || response.headers['Content-Disposition']
            );
            const file = new Blob([response.data], mimeType !== undefined ? { type: mimeType?.toString() } : {});

            if (openInNewTab) {
                const href = URL.createObjectURL(file);
                // Firefox hack: for some reason firefox blocks the window.open call without this dumb, unnecessary await
                await new Promise((res) => setTimeout(res, 0));
                window.open(href, '_blank');
                URL.revokeObjectURL(href);
            } else {
                saveBlobAsFile(
                    file,
                    fileName !== undefined ? fileName : contentDispositionFilename || crypto.randomUUID()
                );
            }
        })
        .catch(async (error: AxiosError<Blob>) => {
            const responseText = error.response?.data ? await error.response.data.text() : '';
            let response: { message?: string; requestId?: string } = {};
            if (responseText) {
                try {
                    const parsed: unknown = JSON.parse(responseText);
                    if (parsed && typeof parsed === 'object') {
                        response = parsed as { message?: string; requestId?: string };
                    }
                } catch {
                    // Non-JSON error payload — fall back to the empty response so errorAlert still fires below.
                    response = {};
                }
            }
            const shouldDisplayAxiosError =
                typeof response?.message === 'string' && response?.message !== 'No message available';
            const message = shouldDisplayAxiosError ? errorMessage(response) : error.message;
            if (rethrowErrors) {
                throw new Error(message);
            }
            errorAlert(message, {
                requestId: response.requestId,
            });
        });
};

/**
 *
 * @param url api endpoint. example: /v1/...
 * @param fileName must contain file extension
 * @param options set rethrowErrors to handle the failure (e.g. show it inline) instead of the
 * default error toast; the rejection carries the backend's error message when one is available
 * @returns {void}
 */
export const downloadFile = async (
    url: string,
    fileName: string | undefined = undefined,
    options: { rethrowErrors?: boolean } = {}
): Promise<void> => {
    return downloadFileRaw(`${import.meta.env.VITE_API_URL}${url}`, fileName, false, options.rethrowErrors ?? false);
};

/**
 * Opens a file in the new tab
 * @param url api endpoint. example: /v1/...
 * @returns {void}
 */
export const openFile = async (url: string): Promise<void> => {
    return downloadFileRaw(`${import.meta.env.VITE_API_URL}${url}`, '', true);
};

export const saveBlobAsFile = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const extractFilename = (contentDisposition: string | undefined): string | null => {
    if (contentDisposition === undefined) {
        return null;
    }
    const extendedMatch = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (extendedMatch) {
        try {
            return decodeURIComponent(extendedMatch[1].trim());
        } catch {
            // Malformed percent-encoding — fall back to the plain `filename` below.
        }
    }
    const match = contentDisposition.match(/filename="?([^";]+)"?/);
    return match ? match[1] : null;
};
